import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

// LV1 Group navigation links
const navLinks = [
  { label: 'Brands', href: '/brands', isPage: true },
  { label: 'Our Method', href: '#method', isPage: false },
  { label: 'Our Showroom', href: '/showroom', isPage: true },
  { label: 'LV1 Throughout the Decades', href: '/history', isPage: true },
  { label: 'About', href: '/about', isPage: true },
  { label: 'Contact', href: '/contact', isPage: true },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleHashClick = (e, href) => {
    if (location.pathname === '/') {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const renderNavLink = (link, isMobile = false) => {
    const baseClass = isMobile
      ? 'text-[#9a9a9a] hover:text-white transition-colors text-[15px] py-2'
      : 'text-[#9a9a9a] hover:text-white transition-colors duration-300 text-[14px] font-medium whitespace-nowrap';

    const activeClass = location.pathname === link.href ? 'text-white' : '';

    if (link.isPage) {
      return (
        <Link
          key={link.label}
          to={link.href}
          className={`${baseClass} ${activeClass}`}
          onClick={() => isMobile && setMobileMenuOpen(false)}
        >
          {link.label}
        </Link>
      );
    } else {
      // Hash link - scroll on homepage, navigate + scroll on other pages
      if (location.pathname === '/') {
        return (
          <a
            key={link.label}
            href={link.href}
            className={baseClass}
            onClick={(e) => {
              handleHashClick(e, link.href);
              if (isMobile) setMobileMenuOpen(false);
            }}
          >
            {link.label}
          </a>
        );
      } else {
        return (
          <Link
            key={link.label}
            to={`/${link.href}`}
            className={baseClass}
            onClick={() => isMobile && setMobileMenuOpen(false)}
          >
            {link.label}
          </Link>
        );
      }
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-[#0a0a0a]/95 backdrop-blur-lg border-b border-white/[0.06]' : 'bg-transparent'
      }`}
    >
      {/* Logo Container - Positioned to true top-left of viewport */}
      <Link
        to="/"
        className="absolute left-4 md:left-5 top-2.5 md:top-3 flex items-center p-2 group z-10"
        style={{
          cursor: 'pointer',
        }}
      >
        <img
          src="/logos-for-web/LV1GROUP.png"
          alt="LV1 Group"
          className="h-[36px] md:h-[44px] w-auto max-w-[190px] md:max-w-[230px] object-contain"
          style={{
            imageRendering: 'auto',
            opacity: 0.85,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.75';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.85';
          }}
        />
      </Link>

      <div className="max-w-[1320px] mx-auto px-6 py-3 flex items-center justify-end relative">
        {/* Desktop Navigation - Centered independently */}
        <div className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
          {navLinks.map((link) => renderNavLink(link))}
        </div>

        {/* CTA Button */}
        <Link
          to="/contact"
          className="hidden md:flex items-center px-5 py-2.5 bg-white text-[#0a0a0a] rounded-full text-[14px] font-medium hover:bg-white/90 transition-all duration-300"
        >
          Get in Touch with LV1
        </Link>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden text-white p-2 hover:bg-white/5 rounded-lg transition-colors"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenuOpen ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#0a0a0a]/98 backdrop-blur-lg border-t border-white/[0.06] px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => renderNavLink(link, true))}
          <Link
            to="/contact"
            className="flex items-center justify-center px-5 py-3 bg-white text-[#0a0a0a] rounded-full text-[14px] font-medium hover:bg-white/90 transition-all mt-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            Get in Touch with LV1
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
